import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditTextareaField } from '../../../EditTextareaField';
import type { ContactNotes } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactNotes;
}

const NOTES_FIELDS: FieldMapping[] = [
  { apiField: 'description', formField: 'description' },
  { apiField: 'biography', formField: 'biography' },
  { apiField: 'shortBiography', formField: 'shortBiography' },
  { apiField: 'summaryNotes', formField: 'summaryNotes' },
  { apiField: 'pmiNotes', formField: 'pmiNotes' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactNotes,
  initial: ContactNotes
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        description
        biography
        shortBiography
        summaryNotes
        pmiNotes
      }
    }
  }
`;

export interface EditNotesDialogProps {
  contactId: string;
  initialValue: ContactNotes;
  isOpen: boolean;
  onChange: (data: ContactNotes) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactNotes;
}

export function EditNotesDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditNotesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(NOTES_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactNotes>(
    field: K,
    fieldValue: ContactNotes[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save notes');
      } else {
        toast.success('Notes saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Notes & Biography"
    >
      <div className="space-y-6">
        <EditTextareaField
          label="Description"
          onChange={description => {
            updateField('description', description);
          }}
          rows={3}
          value={value.description}
        />
        <EditTextareaField
          label="Biography"
          onChange={biography => {
            updateField('biography', biography);
          }}
          rows={4}
          value={value.biography}
        />
        <EditTextareaField
          label="Short Biography (for Liquidity Report)"
          onChange={shortBiography => {
            updateField('shortBiography', shortBiography);
          }}
          rows={3}
          value={value.shortBiography}
        />
        <EditTextareaField
          label="Summary Notes"
          onChange={summaryNotes => {
            updateField('summaryNotes', summaryNotes);
          }}
          rows={4}
          value={value.summaryNotes}
        />
        <EditTextareaField
          label="PMI Notes"
          onChange={pmiNotes => {
            updateField('pmiNotes', pmiNotes);
          }}
          rows={4}
          value={value.pmiNotes}
        />
      </div>
    </EditDialog>
  );
}
